import { useState } from 'react';
import { Alert, App, Checkbox, Modal, Space, Typography } from 'antd';
import type { EmailStatus, VerificationStatus, VerifyVendor } from '@crm/shared';
import { errorMessage } from '../../api/client';
import { STATUS_LABEL, useVerifySelection } from '../../features/verification/api';
import { plural } from '../../utils/format';

/** Statuses that can be re-checked; Suppressed (bounced, complained, unsubscribed) is never re-verified. */
const BATCHES: { status: EmailStatus; note: string }[] = [
  { status: 'UNKNOWN', note: 'Checks passed, mailbox not confirmed' },
  { status: 'CATCH_ALL', note: 'Domain accepts any address' },
  { status: 'VALID', note: 'Confirmed earlier; re-check addresses that may have gone stale' },
  { status: 'INVALID', note: 'Will bounce; re-check in case a domain came back' },
  { status: 'DISPOSABLE', note: 'Throwaway mailbox provider' },
  { status: 'UNVERIFIED', note: 'Not checked yet' },
];

/** Pick which status groups to verify again, even if they were already verified. Uses the checker chosen on the page. */
export function ReverifyModal({ open, onClose, status, vendor, vendorLabel }: { open: boolean; onClose: () => void; status: VerificationStatus | undefined; vendor: VerifyVendor; vendorLabel: string }) {
  const { message } = App.useApp();
  const verify = useVerifySelection();
  const [picked, setPicked] = useState<EmailStatus[]>([]);
  const counts = status?.counts ?? {};
  const total = picked.reduce((n, s) => n + (counts[s] ?? 0), 0);
  const builtinOnly = vendor === 'builtin' || (vendor === 'auto' && !status?.autoVendor);

  const close = () => {
    if (verify.isPending) return;
    setPicked([]);
    onClose();
  };
  const start = () =>
    verify
      .mutateAsync({ selection: { all: {} }, vendor, statuses: picked })
      .then((j) => {
        message.success(`Queued ${plural(j.total, 'contact')} for re-verification`);
        close();
      })
      .catch((e) => message.error(errorMessage(e)));

  return (
    <Modal
      open={open}
      title="Re-verify contacts"
      onCancel={close}
      onOk={start}
      okText={total ? `Re-verify ${plural(total, 'contact')}` : 'Re-verify'}
      okButtonProps={{ disabled: !total, loading: verify.isPending }}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        Choose which groups to check again. Every contact in the groups you pick is checked, even if it was verified before. Checker: <b>{vendorLabel}</b> (change it on the page).
      </Typography.Paragraph>
      <Checkbox.Group value={picked} onChange={(v) => setPicked(v as EmailStatus[])} style={{ width: '100%' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {BATCHES.map(({ status: s, note }) => (
            <Checkbox key={s} value={s} disabled={!counts[s]}>
              <b>{STATUS_LABEL[s]}</b> ({(counts[s] ?? 0).toLocaleString()}) <Typography.Text type="secondary">· {note}</Typography.Text>
            </Checkbox>
          ))}
        </Space>
      </Checkbox.Group>
      {builtinOnly && picked.some((s) => s !== 'INVALID' && s !== 'DISPOSABLE' && s !== 'UNVERIFIED') && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          title="Built-in checks can't confirm mailboxes"
          description="They only catch addresses that have become invalid; a mailbox a vendor confirmed keeps its result. Pick a vendor on the page to confirm mailboxes."
        />
      )}
      {!builtinOnly && total > 0 && (
        <Alert type="warning" showIcon style={{ marginTop: 16 }} title={`Uses up to ${total.toLocaleString()} vendor credits`} description="Addresses that fail the free built-in checks first don't use a credit." />
      )}
    </Modal>
  );
}
